import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
} from "recharts";
import {
  Users,
  Gauge,
  TrendingUp,
  Layers,
  LogOut,
  Download,
  Search,
  Eye,
  Loader2,
  FileSpreadsheet,
  FileText,
  Mic,
} from "lucide-react";
import { api, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const COLORS = ["#3B82F6", "#8B5CF6", "#22C55E", "#F59E0B", "#EF4444", "#06B6D4", "#EC4899", "#A3E635"];
const CAT_COLORS = {
  "Highly Systemized Business": "#22C55E",
  "Moderate Operational Challenges": "#3B82F6",
  "Founder Dependency is High": "#F59E0B",
  "Business Depends Almost Entirely on the Founder": "#EF4444",
};

const CATEGORIES = [
  "Highly Systemized Business",
  "Moderate Operational Challenges",
  "Founder Dependency is High",
  "Business Depends Almost Entirely on the Founder",
];
const LANG_LABEL = { en: "English", hi: "हिंदी", ta: "தமிழ்" };
const LANG_OPTS = [
  { code: "en", name: "English" },
  { code: "hi", name: "हिंदी" },
  { code: "ta", name: "தமிழ்" },
];

function Panel({ title, children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-white/10 bg-[#0F1629] p-5 ${className}`}>
      {title && <h3 className="mb-4 text-sm font-semibold text-slate-200">{title}</h3>}
      {children}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F1629] p-5" data-testid={`stat-${label}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-slate-400">{label}</span>
        <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${tint}22`, color: tint }}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <p className="mt-3 font-display text-3xl font-bold text-white">{value}</p>
    </div>
  );
}

const chartTooltip = {
  contentStyle: { background: "#0A0F1C", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, color: "#fff", fontSize: 12 },
  labelStyle: { color: "#94a3b8" },
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [responses, setResponses] = useState([]);
  const [filters, setFilters] = useState({ search: "", industry_type: "", score_category: "", employee_count: "", monthly_turnover_range: "", source: "", selected_language: "" });
  const [detail, setDetail] = useState(null);
  const [loadingResp, setLoadingResp] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login");
    }
  }, [navigate]);

  const loadResponses = useCallback(async () => {
    setLoadingResp(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/admin/responses", { params });
      setResponses(data.responses);
    } catch (e) {
      toast.error("Could not load responses");
    } finally {
      setLoadingResp(false);
    }
  }, [filters]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);
  useEffect(() => {
    const t = setTimeout(loadResponses, 300);
    return () => clearTimeout(t);
  }, [loadResponses]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/responses/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error("Could not load response");
    }
  };

  const doExport = async (fmt) => {
    try {
      const params = new URLSearchParams({ format: fmt, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) });
      const res = await fetch(`${API}/admin/export?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fmt === "xlsx" ? "decisionos_responses.xlsx" : "decisionos_responses.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const categoryData = useMemo(() => {
    if (!stats) return [];
    return CATEGORIES.map((c) => ({ name: c, short: c.split(" ").slice(0, 2).join(" "), count: stats.category_counts?.[c] || 0 }));
  }, [stats]);

  if (!stats) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0F1C]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const selCls = "h-9 border-white/15 bg-white/5 text-sm text-white";

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <header className="sticky top-0 z-20 border-b border-white/10 bg-[#0A0F1C]/85 px-6 py-4 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-violet-500 font-display font-bold text-white">D</div>
            <span className="font-display text-lg font-semibold text-white">Admin Dashboard</span>
          </div>
          <button data-testid="logout-btn" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-white transition-colors hover:bg-white/10">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-8">
        {/* stat cards */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={Users} label="Total Responses" value={stats.total_responses} tint="#3B82F6" />
          <StatCard icon={Gauge} label="Avg Pain Score" value={stats.average_score} tint="#8B5CF6" />
          <StatCard icon={TrendingUp} label="High Dependency" value={(stats.category_counts?.["Founder Dependency is High"] || 0) + (stats.category_counts?.["Business Depends Almost Entirely on the Founder"] || 0)} tint="#F59E0B" />
          <StatCard icon={Layers} label="Industries" value={stats.industry_counts?.length || 0} tint="#22C55E" />
        </div>

        <Tabs defaultValue="analytics" className="mt-8">
          <TabsList className="bg-[#0F1629]">
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="responses" data-testid="tab-responses">Responses</TabsTrigger>
          </TabsList>

          {/* ---------------- ANALYTICS ---------------- */}
          <TabsContent value="analytics" className="mt-6 space-y-6">
            {stats.total_responses === 0 ? (
              <Panel><p className="py-10 text-center text-slate-400">No responses yet. Share your survey link to start collecting data.</p></Panel>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <Panel title="Founder Pain Score distribution">
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={stats.score_distribution}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="range" tick={{ fill: "#94a3b8", fontSize: 12 }} />
                        <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 12 }} />
                        <Tooltip {...chartTooltip} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                        <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                          {stats.score_distribution.map((e, i) => (
                            <Cell key={e.range} fill={COLORS[i % COLORS.length]} />
                          ))}
                          <LabelList dataKey="count" position="top" fill="#e2e8f0" fontSize={12} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Panel>

                  <Panel title="Founder dependency category">
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie data={categoryData} dataKey="count" nameKey="short" cx="50%" cy="50%" outerRadius={95} label={(e) => e.count > 0 ? e.count : ""}>
                          {categoryData.map((e) => (
                            <Cell key={e.name} fill={CAT_COLORS[e.name]} />
                          ))}
                        </Pie>
                        <Tooltip {...chartTooltip} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-2 space-y-1.5">
                      {categoryData.map((c) => (
                        <div key={c.name} className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-2 text-slate-300">
                            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: CAT_COLORS[c.name] }} />
                            {c.name}
                          </span>
                          <span className="text-slate-400">{c.count}</span>
                        </div>
                      ))}
                    </div>
                  </Panel>

                  <Panel title="Industry-wise pain score (avg)">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={stats.industry_scores} layout="vertical" margin={{ left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 12 }} domain={[0, 64]} />
                        <YAxis type="category" dataKey="name" width={120} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                        <Tooltip {...chartTooltip} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                        <Bar dataKey="avg_score" fill="#8B5CF6" radius={[0, 6, 6, 0]}>
                          <LabelList dataKey="avg_score" position="right" fill="#e2e8f0" fontSize={11} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Panel>

                  <Panel title="Employee size vs pain score (avg)">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={stats.employee_scores}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                        <YAxis tick={{ fill: "#94a3b8", fontSize: 12 }} domain={[0, 64]} />
                        <Tooltip {...chartTooltip} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                        <Bar dataKey="avg_score" fill="#22C55E" radius={[6, 6, 0, 0]}>
                          <LabelList dataKey="avg_score" position="top" fill="#e2e8f0" fontSize={11} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Panel>

                  <Panel title="Turnover vs pain score (avg)">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={stats.turnover_scores} layout="vertical" margin={{ left: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 12 }} domain={[0, 64]} />
                        <YAxis type="category" dataKey="name" width={110} tick={{ fill: "#94a3b8", fontSize: 10 }} />
                        <Tooltip {...chartTooltip} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                        <Bar dataKey="avg_score" fill="#F59E0B" radius={[0, 6, 6, 0]}>
                          <LabelList dataKey="avg_score" position="right" fill="#e2e8f0" fontSize={11} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Panel>

                  <Panel title="Where AI should help first (Q16)">
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={stats.ai_focus} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} label={(e) => e.count > 0 ? e.count : ""}>
                          {stats.ai_focus.map((e, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip {...chartTooltip} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-2 grid grid-cols-2 gap-1.5">
                      {stats.ai_focus.map((c, i) => (
                        <div key={c.name} className="flex items-center gap-2 text-xs text-slate-300">
                          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                          {c.name}
                        </div>
                      ))}
                    </div>
                  </Panel>
                </div>

                {/* source & campaign */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                  <Panel title="Responses by source">
                    {stats.source_counts.map((s, i) => (
                      <RowBar key={s.name} label={s.name} value={s.count} max={stats.total_responses} color={COLORS[i % COLORS.length]} />
                    ))}
                  </Panel>
                  <Panel title="Responses by campaign">
                    {stats.campaign_counts.length === 0 ? (
                      <p className="py-6 text-center text-xs text-slate-500">No campaign tags yet</p>
                    ) : stats.campaign_counts.map((s, i) => (
                      <RowBar key={s.name} label={s.name} value={s.count} max={stats.total_responses} color={COLORS[i % COLORS.length]} />
                    ))}
                  </Panel>
                  <Panel title="Responses by city">
                    {stats.city_counts.slice(0, 8).map((s, i) => (
                      <RowBar key={s.name} label={s.name} value={s.count} max={stats.total_responses} color={COLORS[i % COLORS.length]} />
                    ))}
                  </Panel>
                </div>

                {/* language & voice */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                  <Panel title="Responses by language">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={stats.language_counts} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.count > 0 ? e.count : ""}>
                          {stats.language_counts.map((e, i) => (<Cell key={e.code || e.name} fill={COLORS[i % COLORS.length]} />))}
                        </Pie>
                        <Tooltip {...chartTooltip} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-1 space-y-1">
                      {stats.language_counts.map((c, i) => (
                        <div key={c.code || c.name} className="flex items-center justify-between text-xs text-slate-300">
                          <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />{c.name}</span>
                          <span className="text-slate-400">{c.count}</span>
                        </div>
                      ))}
                    </div>
                  </Panel>
                  <Panel title="Voice vs typed answers">
                    <div className="flex h-[220px] flex-col items-center justify-center gap-4">
                      <div className="flex w-full items-center justify-around">
                        <div className="text-center">
                          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-500/15 text-violet-300"><Mic className="h-6 w-6" /></div>
                          <p className="mt-3 font-display text-3xl font-bold text-white">{stats.voice_usage.voice}</p>
                          <p className="text-xs text-slate-400">used voice</p>
                        </div>
                        <div className="text-center">
                          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-500/15 text-blue-300"><FileText className="h-6 w-6" /></div>
                          <p className="mt-3 font-display text-3xl font-bold text-white">{stats.voice_usage.text_only}</p>
                          <p className="text-xs text-slate-400">typed only</p>
                        </div>
                      </div>
                    </div>
                  </Panel>
                  <Panel title="Responses by device">
                    {stats.device_counts.map((s, i) => (
                      <RowBar key={s.name} label={s.name} value={s.count} max={stats.total_responses} color={COLORS[i % COLORS.length]} />
                    ))}
                  </Panel>
                </div>

                {/* pain areas + per question */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <Panel title="Most common pain areas (highest avg score)">
                    <div className="space-y-3">
                      {stats.pain_areas.map((p) => (
                        <div key={p.id} className="flex items-start justify-between gap-3">
                          <span className="text-sm text-slate-300">Q{p.id}. {p.question}</span>
                          <Badge className="shrink-0 bg-amber-500/15 text-amber-300 hover:bg-amber-500/15">{p.avg_score}</Badge>
                        </div>
                      ))}
                    </div>
                  </Panel>
                  <Panel title="Most selected answer per question">
                    <div className="max-h-[340px] space-y-2 overflow-y-auto pr-1">
                      {stats.per_question_top.map((p) => (
                        <div key={p.id} className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
                          <p className="text-xs text-slate-400">Q{p.id}. {p.question}</p>
                          <p className="mt-1 text-sm font-medium text-slate-100">
                            {p.top_answer} <span className="text-slate-500">· {p.count} picks</span>
                          </p>
                        </div>
                      ))}
                    </div>
                  </Panel>
                </div>
              </>
            )}
          </TabsContent>

          {/* ---------------- RESPONSES ---------------- */}
          <TabsContent value="responses" className="mt-6">
            <Panel>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <Input
                    data-testid="search-input"
                    value={filters.search}
                    onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                    placeholder="Search company, founder, email, city…"
                    className="h-9 border-white/15 bg-white/5 pl-9 text-sm text-white placeholder:text-slate-500"
                  />
                </div>
                <div className="flex gap-2">
                  <button data-testid="export-csv-btn" onClick={() => doExport("csv")} className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm text-white transition-colors hover:bg-white/10">
                    <FileText className="h-4 w-4" /> CSV
                  </button>
                  <button data-testid="export-xlsx-btn" onClick={() => doExport("xlsx")} className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-violet-600 px-4 py-2 text-sm font-medium text-white">
                    <FileSpreadsheet className="h-4 w-4" /> Excel
                  </button>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-6">
                <FilterSelect testid="filter-category" value={filters.score_category} onChange={(v) => setFilters((f) => ({ ...f, score_category: v }))} placeholder="Category" options={CATEGORIES} cls={selCls} />
                <FilterSelect testid="filter-industry" value={filters.industry_type} onChange={(v) => setFilters((f) => ({ ...f, industry_type: v }))} placeholder="Industry" options={(stats.industry_counts || []).map((c) => c.name)} cls={selCls} />
                <FilterSelect testid="filter-employee" value={filters.employee_count} onChange={(v) => setFilters((f) => ({ ...f, employee_count: v }))} placeholder="Employees" options={["1–10", "11–25", "26–50", "More than 50"]} cls={selCls} />
                <FilterSelect testid="filter-turnover" value={filters.monthly_turnover_range} onChange={(v) => setFilters((f) => ({ ...f, monthly_turnover_range: v }))} placeholder="Turnover" options={["Below ₹5 lakh", "₹5 lakh – ₹25 lakh", "₹25 lakh – ₹1 crore", "₹1 crore – ₹5 crore", "Above ₹5 crore"]} cls={selCls} />
                <FilterSelect testid="filter-source" value={filters.source} onChange={(v) => setFilters((f) => ({ ...f, source: v }))} placeholder="Source" options={(stats.source_counts || []).map((c) => c.name)} cls={selCls} />
                <Select value={filters.selected_language || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, selected_language: v === "all" ? "" : v }))}>
                  <SelectTrigger data-testid="filter-language" className={selCls}><SelectValue placeholder="Language" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Language</SelectItem>
                    {LANG_OPTS.map((o) => (<SelectItem key={o.code} value={o.code}>{o.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-5 overflow-x-auto">
                <table className="w-full min-w-[820px] text-left text-sm" data-testid="responses-table">
                  <thead>
                    <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-slate-400">
                      <th className="py-3 pr-4">Company</th>
                      <th className="py-3 pr-4">Founder</th>
                      <th className="py-3 pr-4">City</th>
                      <th className="py-3 pr-4">Industry</th>
                      <th className="py-3 pr-4">Score</th>
                      <th className="py-3 pr-4">Category</th>
                      <th className="py-3 pr-4">Source</th>
                      <th className="py-3 pr-4">Language</th>
                      <th className="py-3 pr-4">Voice</th>
                      <th className="py-3 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingResp ? (
                      <tr><td colSpan={10} className="py-10 text-center text-slate-500"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
                    ) : responses.length === 0 ? (
                      <tr><td colSpan={10} className="py-10 text-center text-slate-500">No responses found</td></tr>
                    ) : (
                      responses.map((r) => (
                        <tr key={r.respondent_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`response-row-${r.respondent_id}`}>
                          <td className="py-3 pr-4 font-medium text-slate-100">{r.company_name}</td>
                          <td className="py-3 pr-4 text-slate-300">{r.founder_name}</td>
                          <td className="py-3 pr-4 text-slate-400">{r.city}</td>
                          <td className="py-3 pr-4 text-slate-400">{r.industry_type}</td>
                          <td className="py-3 pr-4"><span className="font-display font-bold text-white">{r.total_score}</span></td>
                          <td className="py-3 pr-4">
                            <span className="rounded-full px-2 py-0.5 text-xs font-medium" style={{ background: `${CAT_COLORS[r.score_category]}22`, color: CAT_COLORS[r.score_category] }}>
                              {r.score_category.split(" ").slice(0, 2).join(" ")}
                            </span>
                          </td>
                          <td className="py-3 pr-4 text-slate-400">{r.source}</td>
                          <td className="py-3 pr-4 text-slate-300">{LANG_LABEL[r.selected_language] || r.selected_language}</td>
                          <td className="py-3 pr-4">
                            {r.voice_used ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 px-2 py-0.5 text-xs text-violet-300">
                                <Mic className="h-3 w-3" /> Voice
                              </span>
                            ) : (
                              <span className="text-xs text-slate-500">Typed</span>
                            )}
                          </td>
                          <td className="py-3 pr-4">
                            <button data-testid={`view-btn-${r.respondent_id}`} onClick={() => openDetail(r.respondent_id)} className="inline-flex items-center gap-1 rounded-lg bg-white/5 px-3 py-1.5 text-xs text-white hover:bg-white/10">
                              <Eye className="h-3.5 w-3.5" /> View
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-slate-500">{responses.length} response(s) shown</p>
            </Panel>
          </TabsContent>
        </Tabs>
      </div>

      {/* detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-white/10 bg-[#0F1629] text-white">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{detail.respondent.company_name}</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Full survey response · submitted {new Date(detail.submitted_at).toLocaleString()}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4" data-testid="detail-content">
                <div className="grid grid-cols-2 gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4 text-sm">
                  <Info label="Founder" value={detail.respondent.founder_name} />
                  <Info label="Email" value={detail.respondent.email} />
                  <Info label="Mobile" value={detail.respondent.mobile_number} />
                  <Info label="City / State" value={`${detail.respondent.city}, ${detail.respondent.state}`} />
                  <Info label="Industry" value={detail.respondent.industry_type} />
                  <Info label="Employees" value={detail.respondent.employee_count} />
                  <Info label="Turnover" value={detail.respondent.monthly_turnover_range} />
                  <Info label="Source" value={detail.respondent.source || "direct"} />
                  <Info label="Language" value={LANG_LABEL[detail.selected_language] || detail.selected_language} />
                </div>
                <div className="flex items-center justify-between rounded-xl border p-4" style={{ borderColor: `${CAT_COLORS[detail.score_category]}55`, background: `${CAT_COLORS[detail.score_category]}12` }}>
                  <span className="text-sm text-slate-300">Founder Pain Score</span>
                  <span className="font-display text-2xl font-bold" style={{ color: CAT_COLORS[detail.score_category] }}>
                    {detail.total_score} · {detail.score_category}
                  </span>
                </div>
                <div>
                  <h4 className="mb-2 text-sm font-semibold text-slate-200">Multiple-choice answers</h4>
                  <div className="space-y-1.5">
                    {detail.mcq.map((m) => (
                      <div key={m.id} className="flex items-start justify-between gap-3 rounded-lg bg-white/[0.02] p-2.5 text-sm">
                        <span className="text-slate-400">Q{m.id}. {m.question}</span>
                        <span className="shrink-0 text-slate-100">{m.answer} <span className="text-slate-500">({m.score})</span></span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="mb-2 text-sm font-semibold text-slate-200">Descriptive answers</h4>
                  <div className="space-y-2">
                    {detail.text.map((t) => (
                      <div key={t.id} className="rounded-lg bg-white/[0.02] p-3">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-xs text-slate-400">Q{t.id}. {t.question}</p>
                          {t.voice_used && (
                            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-violet-500/15 px-2 py-0.5 text-[10px] text-violet-300">
                              <Mic className="h-2.5 w-2.5" /> Voice{t.detected_language ? ` · ${t.detected_language}` : ""}
                            </span>
                          )}
                        </div>
                        {t.answer_en && t.answer_en !== t.answer ? (
                          <>
                            <p className="mt-1 text-sm text-slate-100">{t.answer_en}</p>
                            <p className="mt-2 border-t border-white/5 pt-2 text-xs text-slate-500">
                              <span className="font-medium text-slate-400">Original ({detail.selected_language}):</span> {t.answer}
                            </p>
                          </>
                        ) : (
                          <p className="mt-1 text-sm text-slate-100">{t.answer || <span className="text-slate-500">— skipped —</span>}</p>
                        )}
                        {t.voice_used && t.original_transcript && t.original_transcript !== t.answer && (
                          <p className="mt-2 border-t border-white/5 pt-2 text-xs text-slate-500">
                            <span className="font-medium text-slate-400">Voice transcript:</span> {t.original_transcript}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-slate-100">{value}</p>
    </div>
  );
}

function RowBar({ label, value, max, color }) {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div className="mb-2.5">
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="capitalize text-slate-300">{label}</span>
        <span className="text-slate-400">{value}</span>
      </div>
      <div className="h-2 rounded-full bg-white/5">
        <div className="h-2 rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, cls, testid }) {
  return (
    <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger data-testid={testid} className={cls}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All {placeholder}</SelectItem>
        {options.filter(Boolean).map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
